import {  makeAutoObservable } from "mobx";
import {
  AppointmentDto,
  CalendarApi,
  RxDto,
  TimeSlotDto,
  LeaveOfAbsenceDto,
  EventTimeSlotDto,
  ContinuousEventTimeSlotDto,
  CAResponse,
} from "../../api-client";
import AuthStore from "../Auth/authStore";
import notificationStore from "../Notification/notificationStore";
import { BaseStore, WrappedApiClient } from "../BaseStore";
import moment from "moment";
import { createStoreLogger } from "../../helpers/loggers";
import { queryClient } from "../queryClient";
import { calendarKeys } from "./calendar.keys";
import { isSearchParam, searchRoute } from "../../helpers/searchParams/searchParamDecorator";
import loadingStore from "../Loading/loadingStore";
import { TemplateSchedulePayload } from "../../molecules/Calendar/DynamicTemplateProvider/types";
import { ActiveCalendarDialog } from "./calendarDialog.types";

const getSelectedUsersLocal = () => {
  return (JSON.parse(localStorage.getItem("selectedUserIds") || "[]") as string[]) || [];
};

@searchRoute("/termin")
export default class CalendarStore {
  
  public authStore: AuthStore;
  public api: WrappedApiClient<CalendarApi>;
  public apiUnwrapped: CalendarApi;
  public loadingStore = loadingStore; // Reference to global loading store
  protected logger = createStoreLogger(this.constructor.name);

  public constructor(api: CalendarApi, authStore: AuthStore) {
    this.apiUnwrapped = api;
    const handler: ProxyHandler<CalendarApi> = {
      get: (target, prop: string | symbol) => {
        const orig = (target as any)[prop];
        if (typeof orig !== "function") return orig;
        return async (...args: any[]) => {
          const action = String(prop);
          this.logger.info(`→ ${action}()`, args);
          const start = performance.now();
          try {
            const { data } = await orig.apply(target, args);
            const ms = (performance.now() - start).toFixed(2);
            this.logger.debug(`✓ ${action}() [${ms}ms]:`, data);
            return data;
          } catch (err: any) {
            this.logger.error(`✗ ${action}():`, err);
            const msg = err.response?.data?.message ?? `Error in ${action}`;
            notificationStore.showMessage(msg, "error", err);
            throw err;
          }
        };
      },
    };

    // Cast the proxy back to the original client type
    this.api = new Proxy(api, handler) as unknown as WrappedApiClient<CalendarApi>;
    this.authStore = authStore;
    makeAutoObservable(
      this,
      {
        api: false,
        apiUnwrapped: false,
        authStore: false,
        loadingStore: false,
      },
      { autoBind: true }
    );
  }
  // Intentionally DO NOT store UserDto objects here. The store only tracks selected user IDs
  // via columnUserIds. User DTO data must be fetched through React Query hooks
  // (see useUsersByLocation in calendar.queries.ts) to avoid prop drilling and stale caches.

  // --- UI State (moved from components to store) ---
  @isSearchParam<string>({
    setter: "setSelectedDateStr", // ensure updates funnel through guarded setter
  })
  public selectedDateStr: string = moment().format("YYYY-MM-DD");
  public columnUserIds: string[] = []; //getSelectedUsersLocal(); // user ids for therapist columns
  /**
   * The site the calendar is currently viewing (MD-286). Mirrored from the
   * user store by CalendarProvider; used to stamp `locationId` onto every
   * slot-creation payload so slots know which site they were planned at.
   */
  public currentLocationId: string | null = null;
  public isRoleMode: boolean = true; // default to role mode
  public roleForUserList: string = "therapist"; // default role for user list
  public isCancelledView: boolean = false; // original cancelledView
  public showCancellations: boolean = false; // toggle to explicitly show cancellation layer
  public groupCheck: boolean = true; // represents grouping checkbox state (name inferred)
  @isSearchParam()
  public continuousView: boolean = false; // continuous appointments view toggle
  @isSearchParam()
  public patientDrawerOpen: boolean = false;
 
  public cancellationDrawerOpen: boolean = false;

  // When true, the dynamic template widget is summoned even if the patient has
  // no lacking appointments (manual Rx/therapy selection mode).
  public templateWidgetOpen: boolean = false;

  @isSearchParam()
  public targetPatientId: number | undefined = undefined; // this is the main patient and first active tab in drawer
  @isSearchParam()
  public targetPatientLastName: string | undefined = undefined; // last name of the target patient
  @isSearchParam()
  public targetAppointmentId: string | undefined = undefined; // target appointment to scroll to and highlight

  @isSearchParam()
  public secondaryPatientId: number | undefined = undefined; // for comparison / merging scenarios
  public secondaryPatientLastName: string | undefined = undefined; // last name of the secondary patient
  @isSearchParam()
  public selectedPatientId: number | undefined = undefined; // currently selected patient in drawer (either target or secondary)
  public selectedPatientLastName: string | undefined = undefined; // last name of the selected patient
  public selectedScheduledTimeslot: any = undefined; // TimeSlotDto | ContinuousAppointment etc.

  @isSearchParam()
  public selectedRxId: string | undefined = undefined; // RxDto (kept any to avoid circular import surfacing now)

  /**
   * PROTOTYPE FLAG (debug window): render the planning shelf inside the
   * patient drawer instead of as a band above/below the calendar grid. Off by
   * default — the band stays the shipped behaviour until the drawer variant
   * has been used in anger. Persisted so a reload mid-test keeps the choice.
   */
  public shelfInDrawer: boolean =
    localStorage.getItem("mobiliCal.shelfInDrawer") === "true";

  @isSearchParam()
  public serviceSelected: boolean = false; // indicates whether the service tab is selected

  // --- PinBoard State ---
  public pinBoardItems: (AppointmentDto | ContinuousEventTimeSlotDto)[] = []; // temporary holder for appointments

  // --- Action-dialog slice (context menu / hotkey actions) ---
  // One discriminated union instead of a flag per dialog; payloads are
  // captured snapshots, see calendarDialog.types.ts.
  public activeCalendarDialog: ActiveCalendarDialog | null = null;

  public openCalendarDialog = (dialog: ActiveCalendarDialog) => {
    this.activeCalendarDialog = dialog;
  };

  public closeCalendarDialog = () => {
    this.activeCalendarDialog = null;
  };

  // --- CAResponse Dialog State ---
  public caResponseDialogOpen: boolean = false;
  public caResponseData: CAResponse | null = null;

  // --- Notes Dialog State ---
  public notesDialogOpen: boolean = false;
  public notesDialogAppointmentId: string | null = null;

  // --- Template Schedule Dialog State (MD-148) ---
  // Holds the payload for the missing-data dialog opened when a dynamic
  // template is dropped but its address / per-therapy visit fee could not be
  // inferred from the Rx's existing appointments.
  public templateScheduleDialogOpen: boolean = false;
  public templateScheduleDialogData: TemplateSchedulePayload | null = null;

  // --- Route Planning Dialog State ---
  public routePlanningMode: boolean = false;
  public routePlanningUserId: string | null = null;

  // --- Closest Appointments Modal State ---
  public closestAppointmentsModalOpen: boolean = false;
  /**
   * What the modal should search around, set by whichever shelf surface opened
   * it. Explicit rather than derived from the selection fields, because the
   * three surfaces that can open it hold three different handles on the same
   * question: an unscheduled/pinned card knows an appointment id, a Dauertermin
   * card a continuous id, and a primed template chip only an address id.
   *
   * `scope` picks the PLANE to search, which follows the calendar view rather
   * than the target: planning a recurring slot against one particular week's
   * appointments (or vice versa) compares things that don't share a time axis.
   */
  public closestAppointmentsTarget: {
    appointmentId?: string;
    continuousAppointmentId?: string;
    addressId?: string;
    scope: "appointments" | "continuous";
  } | null = null;

  // When true, navigating to a targetAppointmentId will not change selected patient or open drawer
  public peekMode: boolean = false;

  // --- Shelf Selection State (lifted from EventShelf for global access) ---
  public selectedShelfAppointmentId: string | null = null;
  public selectedShelfAppointmentAddress: {
    id: string;
    latitude: number;
    longitude: number;
  } | null = null;
  // Selected Dynamic Template Provider handle (MD-148): the full draggable
  // payload (as built for data-event), so keyboard "belegen" can plan it
  // exactly like a drag-drop. Mutually exclusive with the appointment selection.
  public selectedShelfTemplate: { id: string; [key: string]: any } | null = null;

  // Toggle for working hours editing mode
  public timeSheetMode: boolean = false;

  // Toggle for single therapist weekly view
  public singleTherapistView: boolean = false;
  public singleTherapistUserId: string | undefined = undefined;

  // --- Focus Position State (for keyboard navigation persistence across date changes) ---
  public focusPosition: { resourceId: string; time: string } | null = null;

  // --- Sticky Time Slot Indicator State (for keyboard navigation time display) ---
  public highlightedTimeSlot: string | null = null;
  public isKeyboardNavigation: boolean = false;
  public originalDragTime: string | null = null;

  // --- Mouse Hover Time Slot Indicator State ---
  public isMouseHover: boolean = false;
  public mouseHoverTimeSlot: string | null = null;

  // --- Actions / Mutators ---
  public setSelectedDateStr = (date: string) => {
    if (date && date !== this.selectedDateStr) {
      this.selectedDateStr = date;
    }
  };

  public setFocusPosition = (position: { resourceId: string; time: string } | null) => {
    this.focusPosition = position;
  };

  // --- Sticky Time Slot Indicator Actions ---
  public setHighlightedTimeSlot = (timeSlot: string | null) => {
    this.highlightedTimeSlot = timeSlot;
  };

  public setIsKeyboardNavigation = (isKeyboard: boolean) => {
    this.isKeyboardNavigation = isKeyboard;
  };

  public setOriginalDragTime = (time: string | null) => {
    this.originalDragTime = time;
  };

  // --- Mouse Hover Time Slot Indicator Actions ---
  public setIsMouseHover = (isHover: boolean) => {
    this.isMouseHover = isHover;
  };

  public setMouseHoverTimeSlot = (timeSlot: string | null) => {
    this.mouseHoverTimeSlot = timeSlot;
  };

  /**
   * Updates the UI parameters columnUserIds and persists them to localStorage.
   * Also invalidates the react-query for all UserDto caches
   * @param userIds
   */
  public setColumnUserIds = (userIds: string[]) => {
    this.columnUserIds = userIds;
    // localStorage.setItem("selectedUserIds", JSON.stringify(userIds));
    queryClient.invalidateQueries({ queryKey: calendarKeys.usersByLocation.all() }); // force refetch
  };
  public setCancelledView = (val: boolean) => {
    this.isCancelledView = val;
  };

  /**
   * Visit-type filter of the dashboard cancellation list (MD-290 D-DASH-01).
   * Lives here rather than in component state so leaving the dashboard (or
   * opening an entry) and coming back keeps the chosen view for the working
   * session — the store is app-wide (CalendarProvider wraps the app), so it
   * survives navigation but resets with the tab, exactly the required scope.
   * Values mirror the component's VISIT_FILTERS.
   */
  public cancelledVisitFilter: "alle" | "haus" | "praxis" = "alle";
  public setCancelledVisitFilter = (val: "alle" | "haus" | "praxis") => {
    this.cancelledVisitFilter = val;
  };
  public setShowCancellations = (val: boolean) => {
    this.showCancellations = val;
  };
  public setGroupCheck = (val: boolean) => {
    this.groupCheck = val;
  };

  // Set TimeSheet mode for working hours editing  
  public setTimeSheetMode = (val: boolean) => {
    this.timeSheetMode = val;
  };

  // Set Single Therapist view mode
  public setSingleTherapistView = (val: boolean) => {
    this.singleTherapistView = val;
  };

  public setSingleTherapistUserId = (userId: string | undefined) => {
    this.singleTherapistUserId = userId;
  };
  
  // Optional handler to be called when continuousView changes
  private continuousViewChangeHandler?: (continuous: boolean) => void;
  
  public setContinuousViewChangeHandler = (handler?: (continuous: boolean) => void) => {
    this.continuousViewChangeHandler = handler;
  };
  
  public setContinuousView = (val: boolean) => {
    if (this.continuousView !== val) {
      this.continuousView = val;
      // Vorlagen are unavailable in the continuous view (see
      // useDynamicTemplateProviderController); a chip picked beforehand must
      // not stay armed for the keyboard insert.
      if (val) this.selectedShelfTemplate = null;
      // Call the handler if it's set
      if (this.continuousViewChangeHandler) {
        this.continuousViewChangeHandler(val);
      }
    }
  };
  public setPatientDrawerOpen = (open: boolean) => {
    this.patientDrawerOpen = open;
  };
  public setCancellationDrawerOpen = (open: boolean) => {
    this.cancellationDrawerOpen = open;
  };
  public setTemplateWidgetOpen = (open: boolean) => {
    this.templateWidgetOpen = open;
  };
  public toggleTemplateWidget = () => {
    this.templateWidgetOpen = !this.templateWidgetOpen;
  };
  /**
   * settting the target also selects it
   * @param patientId
   */
  public setTargetPatientId = (patientId: number | undefined) => {
    this.targetPatientId = patientId;
    // this.setSelectedPatientId(patientId);
  };

  public setTargetPatientLastName = (lastName: string|undefined) => {
    if (this.targetPatientLastName != lastName) this.targetPatientLastName = lastName;
    // if (this.selectedPatientLastName != lastName) this.setSelectedPatientLastName(lastName);
  };

  public setTargetAppointmentId = (appointmentId: string | undefined) => {
    this.targetAppointmentId = appointmentId;
  };

  public setSecondaryPatientId = (patientId: number | undefined) => {
    this.secondaryPatientId = patientId;
  };

  public setSecondaryPatientLastName = (lastName: string | undefined) => {
    this.secondaryPatientLastName = lastName;
  };

  /**
   * selects a patient. if we have no target also sets it
   * @param patientId
   */

  public setSelectedPatientId = (patientId: number) => {
    // if (!this.targetPatientId || this.selectedPatientId == this.targetPatientId) {
    //   if (this.targetPatientId != patientId) this.setTargetPatientId(patientId);
    // }
    if (this.selectedPatientId !== patientId) {
      this.selectedPatientId = patientId;
      // Manual template-selection mode is a deliberate per-patient
      // intervention — it must not leak onto the next patient's shelf and
      // make their Vorlagen section open in selection mode uninvited.
      this.templateWidgetOpen = false;
    }
  };

  public setSelectedPatientLastName = (lastName: string) => {
    // if (!this.targetPatientLastName || this.selectedPatientLastName == this.targetPatientLastName) {
    //   if (this.targetPatientLastName != lastName) this.setTargetPatientLastName(lastName);
    // }
    this.selectedPatientLastName = lastName;
  };

  /**
   * The drawer's "secondary" tab patient (the non-target patient shown on the
   * right): the currently selected patient when it differs from the target,
   * otherwise the parked secondaryPatientId. Undefined when only the target is
   * present. Mirrors the left/right tab resolution in CalendarDrawer.
   */
  public get secondaryTabPatientId(): number | undefined {
    const target = this.targetPatientId;
    if (!target) return undefined;
    if (this.selectedPatientId && this.selectedPatientId !== target) return this.selectedPatientId;
    if (this.secondaryPatientId && this.secondaryPatientId !== target) return this.secondaryPatientId;
    return undefined;
  }

  /**
   * Make `patientId` the active drawer tab, keeping the target/secondary
   * bookkeeping in sync exactly like PatientDrawer's tab click: selecting the
   * target parks the previous selection as the secondary, selecting a
   * non-target clears the secondary.
   */
  public selectDrawerPatient = (patientId: number, lastName: string) => {
    if (this.selectedPatientId) {
      if (patientId === this.targetPatientId) {
        this.setSecondaryPatientId(this.selectedPatientId);
        this.setSecondaryPatientLastName(this.selectedPatientLastName || "");
      } else {
        this.setSecondaryPatientId(undefined);
        this.setSecondaryPatientLastName("");
      }
    }
    this.setSelectedPatientId(patientId);
    this.setSelectedPatientLastName(lastName || "");
  };

  /**
   * Toggle the active drawer tab between the primary (target) patient and the
   * secondary (non-target) patient. No-op when there is no secondary patient.
   */
  public toggleSelectedPatient = () => {
    const target = this.targetPatientId;
    if (!target) return;
    const secondaryId = this.secondaryTabPatientId;
    if (!secondaryId) return; // nothing to toggle to
    if (this.selectedPatientId === target) {
      this.selectDrawerPatient(secondaryId, this.secondaryPatientLastName || "");
    } else {
      this.selectDrawerPatient(target, this.targetPatientLastName || "");
    }
  };

  /**
   * Clear the drawer's secondary (non-target) patient and make the target the
   * active tab again. Returns true when a secondary patient was actually
   * cleared (used by the Escape key chain to decide whether to stop).
   */
  public clearSecondaryPatient = (): boolean => {
    const target = this.targetPatientId;
    if (!target) return false;
    const secondaryId = this.secondaryTabPatientId;
    if (!secondaryId) return false;
    this.secondaryPatientId = undefined;
    this.secondaryPatientLastName = undefined;
    if (this.selectedPatientId !== target) {
      this.setSelectedPatientId(target);
      this.setSelectedPatientLastName(this.targetPatientLastName || "");
    }
    return true;
  };

  public setIsRoleMode = (isRole: boolean) => {
    this.isRoleMode = isRole;
  };

  public setRoleForUserList = (role: string) => {
    this.roleForUserList = role;
  };

  public setSelectedScheduledTimeslot = (slot: any) => {
    this.selectedScheduledTimeslot = slot;
  };
  public setShelfInDrawer = (enabled: boolean) => {
    this.shelfInDrawer = enabled;
    localStorage.setItem("mobiliCal.shelfInDrawer", String(enabled));
  };

  public setSelectedRxId = (rxId: string | undefined) => {
    this.selectedRxId = rxId;
    // If an rxId is selected, service should not be selected
    if (rxId) {
      this.serviceSelected = false;
    }
  };

  public setServiceSelected = (selected: boolean) => {
    this.serviceSelected = selected;
    // If service is selected, clear the selectedRxId
    if (selected) {
      this.selectedRxId = undefined;
    }
  };

  // --- PinBoard Actions ---
  public addToPinBoard = (item: AppointmentDto | ContinuousEventTimeSlotDto) => {
    // console.log("Adding to PinBoard:", item);
    if (!this.pinBoardItems.find((existingItem) => existingItem.id === item.id)) {
      this.pinBoardItems = [...this.pinBoardItems, item];
    }
  };

  public removeFromPinBoard = (itemId: string) => {
    const isDirectPin = this.pinBoardItems.some((item) => item.id === itemId);
    if (isDirectPin) {
      this.pinBoardItems = this.pinBoardItems.filter((item) => item.id !== itemId);
      // A selection pointing at a removed pin is stale — drop it.
      if (this.selectedShelfAppointmentId === itemId) {
        this.setSelectedShelfAppointment(null, null);
      }
      return;
    }
    // Not a pin id — maybe a SLOT of a whole-appointment pin was rescheduled
    // individually (per-slot handle on an Ablage card). Trim it from its host
    // pin so the pin's drag payload and the calendar's pinned-filter stay
    // truthful (the backend split the slot off the appointment); drop the pin
    // entirely when its last slot is gone.
    const host: any = this.pinBoardItems.find(
      (item: any) => !item.isSlotPin && item.timeSlots?.some((ts: any) => ts.id === itemId)
    );
    if (!host) return;
    const remainingSlots = host.timeSlots.filter((ts: any) => ts.id !== itemId);
    if (remainingSlots.length > 0) {
      this.pinBoardItems = this.pinBoardItems.map((item) =>
        item.id === host.id ? ({ ...item, timeSlots: remainingSlots } as any) : item
      );
    } else {
      this.pinBoardItems = this.pinBoardItems.filter((item) => item.id !== host.id);
      if (this.selectedShelfAppointmentId === host.id) {
        this.setSelectedShelfAppointment(null, null);
      }
    }
  };

  public clearPinBoard = () => {
    if (
      this.selectedShelfAppointmentId &&
      this.pinBoardItems.some((item) => item.id === this.selectedShelfAppointmentId)
    ) {
      this.setSelectedShelfAppointment(null, null);
    }
    this.pinBoardItems = [];
  };

  // --- CAResponse Dialog Actions ---
  public openCAResponseDialog = () => {
    this.caResponseDialogOpen = true;
    // Data will be set separately when available
  };

  public setCAResponseData = (caResponse: CAResponse) => {
    this.caResponseData = caResponse;
  };

  public closeCAResponseDialog = () => {
    this.caResponseDialogOpen = false;
    this.caResponseData = null;
  };

  // --- Notes Dialog Actions ---
  public openNotesDialog = (appointmentId: string) => {
    this.notesDialogOpen = true;
    this.notesDialogAppointmentId = appointmentId;
  };

  public closeNotesDialog = () => {
    this.notesDialogOpen = false;
    this.notesDialogAppointmentId = null;
  };

  // --- Template Schedule Dialog Actions (MD-148) ---
  public openTemplateScheduleDialog = (payload: TemplateSchedulePayload) => {
    this.templateScheduleDialogData = payload;
    this.templateScheduleDialogOpen = true;
  };

  public closeTemplateScheduleDialog = () => {
    this.templateScheduleDialogOpen = false;
    this.templateScheduleDialogData = null;
  };

  // --- Route Planning Actions ---
  public enableRoutePlanning = (userId: string) => {
    this.routePlanningMode = true;
    this.routePlanningUserId = userId;
  };

  public disableRoutePlanning = () => {
    this.routePlanningMode = false;
    this.routePlanningUserId = null;
  };

  // --- Closest Appointments Modal Actions ---
  public setClosestAppointmentsModalOpen = (open: boolean) => {
    this.closestAppointmentsModalOpen = open;
    if (!open) {
      this.closestAppointmentsTarget = null;
    }
  };

  /** Open the modal around a specific target (see closestAppointmentsTarget). */
  public openClosestAppointments = (
    target: NonNullable<CalendarStore["closestAppointmentsTarget"]>,
  ) => {
    this.closestAppointmentsTarget = target;
    this.closestAppointmentsModalOpen = true;
  };

  // --- Shelf Selection Actions ---
  public setSelectedShelfAppointment = (
    id: string | null,
    address: { id: string; latitude: number; longitude: number } | null,
  ) => {
    this.selectedShelfAppointmentId = id;
    this.selectedShelfAppointmentAddress = address;
    // Shelf selection is exclusive: picking an appointment clears any template pick.
    if (id) {
      this.selectedShelfTemplate = null;
    }
    // Auto-close closest appointments modal when shelf appointment is deselected
    if (!id) {
      this.setClosestAppointmentsModalOpen(false);
    }
  };

  public setSelectedShelfTemplate = (payload: { id: string; [key: string]: any } | null) => {
    this.selectedShelfTemplate = payload;
    // Shelf selection is exclusive: picking a template clears any appointment pick.
    if (payload) {
      this.setSelectedShelfAppointment(null, null);
    } else {
      // Same contract as the appointment side: the modal belongs to the
      // selection that opened it and must not outlive it.
      this.setClosestAppointmentsModalOpen(false);
    }
  };

  // getUsersByLocation migrated to React Query hook (see calendar.queries.ts)
  // Legacy direct API access (if ever needed):
  // this.api.getUsersByLocationForCalendar(locationId, includeOtherUsers, appointmentDto)

  public getPatientsByAppointmentDate = async (date: string) => {
    try {
      const { data } = (await this.apiUnwrapped.getPatientsByAppointmentDateForCalendar(
        date
      )) as any;
      return data || [];
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Fehler beim Abrufen der Patienten nach Termin";
      notificationStore.showMessage(errorMessage, "error", error);
    }
  };

  /**
   * MD-286: stamp the viewed site onto an outgoing slot payload. Central here
   * so none of the many creation call sites need to know about locations.
   * An explicit locationId on the payload wins; slots created outside any
   * location context stay unstamped (NULL = visible at every site).
   */
  private stampLocation = <T extends { locationId?: string | null }>(body: T): T => {
    if (body.locationId === undefined || body.locationId === null) {
      return { ...body, locationId: this.currentLocationId };
    }
    return body;
  };

  public createTimeslot = async (body: TimeSlotDto) => {
    try {
      const { data } = (await this.apiUnwrapped.postTimeslot(this.stampLocation(body))) as any;
      return data || [];
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Fehler beim Erstellen des Zeitfensters";
      notificationStore.showMessage(errorMessage, "error", error);
    }
  };

  public createLeaveOfAbsence = async (leaveOfAbsence: LeaveOfAbsenceDto) => {
    try {
      // NOT stamped with the viewed site (unlike the other creation paths):
      // an absence is person-scoped and must stay visible at every site.
      const { data } = (await this.apiUnwrapped.postLeaveOfAbsence(leaveOfAbsence)) as any;
      return data || [];
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "Fehler beim Erstellen der Abwesenheit";
      notificationStore.showMessage(errorMessage, "error", error);
    }
  };

  public createEventTimeSlot = async (eventTimeSlot: EventTimeSlotDto) => {
    try {
      const { data } = (await this.apiUnwrapped.postEventTimeslot(this.stampLocation(eventTimeSlot))) as any;
      return data || [];
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Fehler beim Erstellen des Ereignis-Zeitfensters";
      notificationStore.showMessage(errorMessage, "error", error);
    }
  };

  public createContinuousEventTimeSlot = async (eventTimeSlot: ContinuousEventTimeSlotDto) => {
    try {
      const { data } = (await this.apiUnwrapped.postContinuousEventTimeslot(eventTimeSlot)) as any;
      return data || [];
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "Fehler beim Erstellen des kontinuierlichen Ereignis-Zeitfensters";
      notificationStore.showMessage(errorMessage, "error", error);
    }
  };

  public getContinuousAppointmentsForPatient = async (patientId: string) => {
    try {
      const { data } = (await this.apiUnwrapped.getContinuousAppointmentsForPatientCalendar(
        patientId
      )) as any;
      return data || [];
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "Fehler beim Abrufen der kontinuierlichen Termine für den Patienten";
      notificationStore.showMessage(errorMessage, "error", error);
      console.error("Error fetching continuous appointments:", error);
    }
  };

  public getContinuousAppointmentsForUser = async (userId: string) => {
    try {
      const { data } = (await this.apiUnwrapped.getContinuousAppointmentsForUserCalendar(
        userId
      )) as any;
      return data || [];
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "Fehler beim Abrufen der kontinuierlichen Termine für den Benutzer";
      notificationStore.showMessage(errorMessage, "error", error);
      console.error("Error fetching continuous appointments:", error);
    }
  };

  public getContinuousAppointment = async (id: string) => {
    try {
      const { data } = (await this.apiUnwrapped.getContinuousAppointment(id)) as any;
      return data || [];
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Fehler beim Abrufen des kontinuierlichen Termins";
      notificationStore.showMessage(errorMessage, "error", error);
    }
  };

  public therapistSick = async (userId: string, date: Date): Promise<number | undefined> => {
    try {
      // const { data } = (await this.apiUnwrapped.therapistSick(userId, date.toUTCString(),date.toUTCString())) as any;
      return 1 as number;
    } catch (error: any) {
      // console.log(error);
      const errorMessage = error.response?.data?.message || "Fehler bei der Krankmeldung";
      notificationStore.showMessage(errorMessage, "error", error);
    }
  };
  public getRxForTimeslot = async (timeslotId: string): Promise<RxDto> => {
    try {
      const { data } = (await this.apiUnwrapped.getRxForTimeSlot(timeslotId)) as any;
      return data;
    } catch (error) {
      notificationStore.showMessage("Fehler beim Laden des Rezepts", "error", error);
      return {} as RxDto;
    }
  };

  public userAbsence = async (
    userId: string,
    leaveOfAbsence: LeaveOfAbsenceDto
  ): Promise<LeaveOfAbsenceDto | undefined> => {
    try {
      const { data } = (await this.apiUnwrapped.userAbsence(userId, leaveOfAbsence)) as any;
      return data as LeaveOfAbsenceDto;
    } catch (error: any) {
      // console.log(error);
      const errorMessage = error.response?.data?.message || "Fehler beim Hinzufügen der Abwesenheit";
      notificationStore.showMessage(errorMessage, "error", error);
    }
  };

  public updateUserAbsence = async (
    userId: string,
    leaveOfAbsence: LeaveOfAbsenceDto
  ): Promise<LeaveOfAbsenceDto | undefined> => {
    try {
      const { data } = (await this.apiUnwrapped.updateUserAbsence(userId, leaveOfAbsence)) as any;
      return data as LeaveOfAbsenceDto;
    } catch (error: any) {
      // console.log(error);
      const errorMessage =
        error.response?.data?.message || "Fehler beim Aktualisieren der Abwesenheit";
      notificationStore.showMessage(errorMessage, "error", error);
    }
  };

  public getUserAbsences = async (userId: string): Promise<LeaveOfAbsenceDto[]> => {
    try {
      const { data } = (await this.apiUnwrapped.getUserAbsences(userId)) as any;
      return data as LeaveOfAbsenceDto[];
    } catch (error: any) {
      // console.log(error);
      const errorMessage = error.response?.data?.message || "Fehler bei der Abwesenheit";
      notificationStore.showMessage(errorMessage, "error");
      return [];
    }
  };

  public getNextAppointmentsByUserId = async (userId: string): Promise<AppointmentDto[]> => {
    try {
      const { data } = (await this.apiUnwrapped.getNextAppointmentsByUserId(userId)) as any;
      return data as AppointmentDto[];
    } catch (error: any) {
      // console.log(error);
      const errorMessage =
        error.response?.data?.message || "Fehler beim Abrufen der nächsten Termine";
      notificationStore.showMessage(errorMessage, "error", error);
      return [];
    }
  };

  getLatestUpdatedAppointments = async (seconds: number) => {
    try {
      const { data } = (await this.apiUnwrapped.getLatestUpdatedAppointments(seconds)) as any;
      return data || [];
    } catch (error: any) {
      const errorMessage =
        error.response.data.message ||
        "Die letzten aktualisierten Termine konnten nicht geladen werden";
      notificationStore.showMessage(errorMessage, "error", error);
    }
  };

  getRouteDurationsForAppointments = async (appointments: AppointmentDto[]) => {
    try {
      const { data } = (await this.apiUnwrapped.getRouteDurationsForAppointments(
        appointments
      )) as any;
      return data || [];
    } catch (error: any) {
      const errorMessage =
        error.response.data.message || "Die Routen-Dauer konnte nicht geladen werden";
      notificationStore.showMessage(errorMessage, "error", error);
    }
  };
}
